/** ADM-24 Stocks · ADM-25 Détail par lots (ajustement, inventaire, transfert) · ADM-26 Mouvements · ADM-27 Transferts · ADM-31 Lots et traçabilité. */
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Modal, Pagination, SearchField, SelectField, StockIndicator, Table, TextField, TextareaField, useToast, type EtatStock } from '@myron/design-system';
import { ArrowLeftRight, Ban, ClipboardCheck, Download, SlidersHorizontal } from 'lucide-react';
import { get, post } from '../../../shared/api/client';
import { SiPermission, useSession } from '../../../shared/auth/session';
import { Confirmation, EntetePage, ErreurFormulaire, EtatRequete, StatutBadge } from '../../../shared/ui/composants';
import { dateCourte, dateHeure, nombre } from '../../../shared/ui/format';
import { csvSur, LIBELLE_MOUVEMENT, SelectEntrepot, telecharger, unites, useEntrepots, type Page } from './commun';

interface StockAgrege { produitId: string; sku: string; libelle: string; entrepotId: string; entrepotCode: string; quantitePhysique: number; quantiteReservee: number; quantiteBloquee: number; disponible: number; etat: EtatStock; sousSeuil: boolean; seuilUnitesBase: number; unitesParPalette: number | null; ddmProchaine: string | null; ddmProche: boolean; nbLots: number }
interface StockLot { lot: { id: string; numeroLot: string; ddm?: string | null; bloque: boolean }; entrepotId: string; emplacement?: string; quantitePhysique: number; quantiteReservee: number; quantiteBloquee: number; disponible: number; version: number; produit: { id: string; sku: string; libelle: string; uniteBase: string; unitesParPalette?: number } }
interface Mouvement { id: string; type: string; produitId: string; libelle?: string; sku?: string; entrepotCode?: string; numeroLot?: string; quantite: number; motif?: string; documentType?: string; auteur: string; validePar?: string; creeLe: string }

const ALERTES = [{ value: '', label: 'Toutes les lignes' }, { value: 'SOUS_SEUIL', label: 'Sous le seuil de réappro' }, { value: 'RUPTURE', label: 'Rupture' }, { value: 'DDM_PROCHE', label: 'DDM < 60 jours' }, { value: 'BLOQUE', label: 'Quantités bloquées' }];

export function PageStocks() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const entrepotId = sp.get('entrepotId') ?? '';
  const alerte = sp.get('alerte') ?? '';
  const q = sp.get('q') ?? '';
  const page = Number(sp.get('page') ?? 1);
  const maj = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); if (k !== 'page') n.delete('page'); setSp(n, { replace: true }); };
  const r = useQuery({ queryKey: ['logistique', 'stocks', entrepotId, alerte, q, page], queryFn: () => get<Page<StockAgrege>>('/admin/stocks', { entrepotId, alerte, q, page, taille: 25 }) });
  return (
    <>
      <EntetePage titre="Stocks" sousTitre="Quantités par produit et entrepôt, en unités de base. Disponible = physique − réservé − bloqué ; la réservation n’intervient qu’après paiement confirmé." />
      <Card>
        <div className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <SelectEntrepot valeur={entrepotId} onChange={(v) => maj('entrepotId', v)} />
          <SelectField label="Alerte" value={alerte} onChange={(e) => maj('alerte', e.target.value)} options={ALERTES} />
          <div className="my-field"><span className="my-field__label">Recherche</span><SearchField label="Rechercher un produit ou un SKU" defaultValue={q} onKeyDown={(e) => { if (e.key === 'Enter') maj('q', (e.target as HTMLInputElement).value); }} onBlur={(e) => maj('q', e.target.value)} /></div>
        </div>
      </Card>
      <div className="my-pile" style={{ marginTop: 16 }}>
        <EtatRequete requete={r} vide={(d) => d.donnees.length === 0} titreVide="Aucun stock" texteVide="Aucune ligne ne correspond aux filtres." enfants={(d) => (
          <>
            <p className="my-petit my-secondaire">{d.total} ligne(s)</p>
            <Table<StockAgrege>
              legende="Stocks par produit et entrepôt"
              lignes={d.donnees}
              cle={(l) => `${l.produitId}:${l.entrepotId}`}
              onLigne={(l) => nav(`/admin/stocks/${l.produitId}/${l.entrepotId}`)}
              colonnes={[
                { cle: 'p', titre: 'Produit', rendu: (l) => <Link to={`/admin/stocks/${l.produitId}/${l.entrepotId}`} onClick={(e) => e.stopPropagation()}>{l.libelle}<br /><span className="my-legende my-secondaire">{l.sku}</span></Link> },
                { cle: 'e', titre: 'Entrepôt', rendu: (l) => <Badge ton="marque">{l.entrepotCode}</Badge> },
                { cle: 'ph', titre: 'Physique', num: true, rendu: (l) => nombre(l.quantitePhysique) },
                { cle: 'r', titre: 'Réservé', num: true, rendu: (l) => nombre(l.quantiteReservee) },
                { cle: 'b', titre: 'Bloqué', num: true, rendu: (l) => (l.quantiteBloquee ? <span className="my-prix-barre" style={{ textDecoration: 'none' }}>{nombre(l.quantiteBloquee)}</span> : '0') },
                { cle: 'd', titre: 'Disponible', num: true, rendu: (l) => <strong>{unites(l.disponible, l.unitesParPalette)}</strong> },
                { cle: 'et', titre: 'État', rendu: (l) => <StockIndicator etat={l.etat} /> },
                { cle: 'ddm', titre: 'DDM la plus proche', rendu: (l) => (l.ddmProchaine ? <>{dateCourte(l.ddmProchaine)} {l.ddmProche && <Badge ton="alerte">Proche</Badge>}</> : '—') },
              ]}
            />
            <Pagination page={d.page} total={d.total} taille={d.taille} onPage={(p) => maj('page', String(p))} />
          </>
        )} />
      </div>
    </>
  );
}

type Action = { type: 'ajuster' | 'inventaire' | 'transferer'; ligne: StockLot } | null;

export function PageStockDetail() {
  const { produitId = '', entrepotId = '' } = useParams();
  const r = useQuery({ queryKey: ['logistique', 'stock', produitId, entrepotId], queryFn: () => get<StockLot[]>(`/admin/stocks/${produitId}/${entrepotId}`) });
  const ent = useEntrepots();
  const [action, setAction] = useState<Action>(null);
  const produit = r.data?.[0]?.produit;
  const code = ent.data?.find((e) => e.id === entrepotId)?.code ?? '';
  return (
    <>
      <EntetePage titre={produit ? `${produit.libelle} — ${code}` : 'Stock par lot'} sousTitre={produit ? `${produit.sku} · unité de base : ${produit.uniteBase.toLowerCase()}${produit.unitesParPalette ? ` · ${nombre(produit.unitesParPalette)} u. par palette` : ''}` : undefined} fil={[{ libelle: 'Stocks', vers: '/admin/stocks' }, { libelle: 'Détail par lot' }]} />
      <EtatRequete requete={r} vide={(d) => d.length === 0} titreVide="Aucun lot" texteVide="Aucun lot de ce produit dans cet entrepôt." enfants={(d) => (
        <div className="my-pile">
          <p className="my-petit my-secondaire">Lots triés FEFO (DDM la plus proche en premier) : c’est l’ordre de sortie appliqué en préparation.</p>
          <Table<StockLot>
            legende="Lots en stock"
            lignes={d}
            cle={(l) => l.lot.id}
            colonnes={[
              { cle: 'lot', titre: 'Lot', rendu: (l) => <><Link to={`/admin/lots/${l.lot.id}`}>{l.lot.numeroLot}</Link>{l.lot.bloque && <> <Badge ton="erreur">Bloqué</Badge></>}</> },
              { cle: 'ddm', titre: 'DDM', rendu: (l) => dateCourte(l.lot.ddm) },
              { cle: 'emp', titre: 'Emplacement', rendu: (l) => l.emplacement ?? '—' },
              { cle: 'ph', titre: 'Physique', num: true, rendu: (l) => nombre(l.quantitePhysique) },
              { cle: 'r', titre: 'Réservé', num: true, rendu: (l) => nombre(l.quantiteReservee) },
              { cle: 'b', titre: 'Bloqué', num: true, rendu: (l) => nombre(l.quantiteBloquee) },
              { cle: 'd', titre: 'Disponible', num: true, rendu: (l) => <strong>{nombre(l.disponible)}</strong> },
              {
                cle: 'a', titre: 'Actions', actions: true, rendu: (l) => (
                  <div className="my-ligne" style={{ justifyContent: 'flex-end' }}>
                    <SiPermission permission="stock.ajuster">
                      <Button compact variante="secondaire" icone={SlidersHorizontal} onClick={() => setAction({ type: 'ajuster', ligne: l })} aria-label={`Ajuster le lot ${l.lot.numeroLot}`}>Ajuster</Button>
                      <Button compact variante="tertiaire" icone={ClipboardCheck} onClick={() => setAction({ type: 'inventaire', ligne: l })} aria-label={`Inventorier le lot ${l.lot.numeroLot}`}>Inventorier</Button>
                    </SiPermission>
                    <SiPermission permission="stock.transferer">
                      <Button compact variante="tertiaire" icone={ArrowLeftRight} disabled={l.disponible <= 0} onClick={() => setAction({ type: 'transferer', ligne: l })} aria-label={`Transférer le lot ${l.lot.numeroLot}`}>Transférer</Button>
                    </SiPermission>
                  </div>
                ),
              },
            ]}
          />
          <Link to={`/admin/mouvements?produitId=${produitId}&entrepotId=${entrepotId}`}>Voir les mouvements de ce produit</Link>
        </div>
      )} />
      {action?.type === 'ajuster' && <ModaleAjustement ligne={action.ligne} onFermer={() => setAction(null)} />}
      {action?.type === 'inventaire' && <ModaleInventaire ligne={action.ligne} onFermer={() => setAction(null)} />}
      {action?.type === 'transferer' && <ModaleTransfert ligne={action.ligne} onFermer={() => setAction(null)} />}
    </>
  );
}

function useAjustement(onFini: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (corps: { produitId: string; entrepotId: string; lotId: string; type: string; quantite: number; motif: string }) => post<{ statut?: string; resume?: string }>('/admin/stocks/ajustements', corps),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['logistique'] });
      if (r && r.statut === 'EN_ATTENTE_VALIDATION') toast({ ton: 'info', titre: 'Double validation requise', texte: 'L’ajustement dépasse le seuil : il sera appliqué après validation par un second responsable.' });
      else toast({ ton: 'succes', titre: 'Ajustement enregistré' });
      onFini();
    },
  });
}

function ModaleAjustement({ ligne, onFermer }: { ligne: StockLot; onFermer: () => void }) {
  const [type, setType] = useState('AJUSTEMENT_NEGATIF');
  const [quantite, setQuantite] = useState('');
  const [motif, setMotif] = useState('');
  const m = useAjustement(onFermer);
  const q = Number(quantite);
  const valide = Number.isInteger(q) && q > 0 && motif.trim().length >= 3;
  return (
    <Modal ouverte titre={`Ajuster le lot ${ligne.lot.numeroLot}`} onFermer={onFermer} actions={<><Button variante="tertiaire" onClick={onFermer}>Annuler</Button><Button disabled={!valide} chargement={m.isPending} onClick={() => m.mutate({ produitId: ligne.produit.id, entrepotId: ligne.entrepotId, lotId: ligne.lot.id, type, quantite: q, motif: motif.trim() })}>Enregistrer</Button></>}>
      <div className="my-form">
        <p className="my-petit">Disponible : <strong>{nombre(ligne.disponible)}</strong> · bloqué : {nombre(ligne.quantiteBloquee)} (unités de base)</p>
        <SelectField label="Type" value={type} onChange={(e) => setType(e.target.value)} options={[{ value: 'AJUSTEMENT_NEGATIF', label: 'Ajustement négatif (casse, perte)' }, { value: 'AJUSTEMENT_POSITIF', label: 'Ajustement positif (trouvé)' }, { value: 'BLOCAGE', label: 'Bloquer (qualité, DDM)' }, { value: 'DEBLOCAGE', label: 'Débloquer' }]} />
        <TextField label="Quantité (unités de base)" type="number" min={1} value={quantite} onChange={(e) => setQuantite(e.target.value)} required />
        <TextareaField label="Motif" value={motif} onChange={(e) => setMotif(e.target.value)} required aide="Obligatoire, conservé dans le journal des mouvements et d’audit." />
        {type === 'AJUSTEMENT_NEGATIF' && <Alert ton="info">Au-delà du seuil paramétré, un ajustement négatif saisi par un responsable d’entrepôt est soumis à double validation.</Alert>}
        <ErreurFormulaire erreur={m.error} />
      </div>
    </Modal>
  );
}

function ModaleInventaire({ ligne, onFermer }: { ligne: StockLot; onFermer: () => void }) {
  const [compte, setCompte] = useState('');
  const m = useAjustement(onFermer);
  const n = Number(compte);
  const ecart = compte === '' || !Number.isInteger(n) || n < 0 ? null : n - ligne.quantitePhysique;
  return (
    <Modal ouverte titre={`Inventaire du lot ${ligne.lot.numeroLot}`} onFermer={onFermer} actions={<><Button variante="tertiaire" onClick={onFermer}>Annuler</Button><Button disabled={!ecart} chargement={m.isPending} onClick={() => ecart && m.mutate({ produitId: ligne.produit.id, entrepotId: ligne.entrepotId, lotId: ligne.lot.id, type: ecart > 0 ? 'AJUSTEMENT_POSITIF' : 'AJUSTEMENT_NEGATIF', quantite: Math.abs(ecart), motif: `Inventaire : ${n} compté(s) pour ${ligne.quantitePhysique} en système (emplacement ${ligne.emplacement ?? '—'})` })}>Enregistrer l’écart</Button></>}>
      <div className="my-form">
        <p>Emplacement <strong>{ligne.emplacement ?? '—'}</strong> · quantité système : <strong>{nombre(ligne.quantitePhysique)}</strong> unités</p>
        <TextField label="Quantité comptée (unités de base)" type="number" min={0} value={compte} onChange={(e) => setCompte(e.target.value)} />
        {ecart !== null && <Alert ton={ecart === 0 ? 'succes' : 'alerte'}>{ecart === 0 ? 'Aucun écart : rien à enregistrer.' : `Écart de ${ecart > 0 ? '+' : ''}${nombre(ecart)} unité(s) : un ajustement ${ecart > 0 ? 'positif' : 'négatif'} sera enregistré.`}</Alert>}
        <ErreurFormulaire erreur={m.error} />
      </div>
    </Modal>
  );
}

function ModaleTransfert({ ligne, onFermer }: { ligne: StockLot; onFermer: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const [cible, setCible] = useState('');
  const [quantite, setQuantite] = useState('');
  // Destinations possibles : tous les entrepôts actifs (identité minimale), même hors de la portée de l'utilisateur.
  const cibles = useQuery({ queryKey: ['logistique', 'entrepots-cibles'], queryFn: () => get<{ id: string; code: string; nom: string }[]>('/admin/entrepots', { usage: 'transfert' }) });
  const options = [{ value: '', label: 'Choisir…' }, ...(cibles.data ?? []).filter((e) => e.id !== ligne.entrepotId).map((e) => ({ value: e.id, label: `${e.code} — ${e.nom}` }))];
  const m = useMutation({
    mutationFn: () => post('/admin/transferts', { entrepotSourceId: ligne.entrepotId, entrepotCibleId: cible, lignes: [{ produitId: ligne.produit.id, lotId: ligne.lot.id, quantite: Number(quantite) }] }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['logistique'] }); toast({ ton: 'succes', titre: 'Transfert créé en brouillon', texte: 'Expédiez-le depuis la liste des transferts.' }); onFermer(); nav('/admin/transferts'); },
  });
  const q = Number(quantite);
  return (
    <Modal ouverte titre={`Transférer le lot ${ligne.lot.numeroLot}`} onFermer={onFermer} actions={<><Button variante="tertiaire" onClick={onFermer}>Annuler</Button><Button disabled={!cible || !(q > 0 && q <= ligne.disponible)} chargement={m.isPending} onClick={() => m.mutate()}>Créer le transfert</Button></>}>
      <div className="my-form">
        <p className="my-petit">Disponible : <strong>{nombre(ligne.disponible)}</strong> unités</p>
        <SelectField label="Entrepôt de destination" value={cible} onChange={(e) => setCible(e.target.value)} options={options} />
        <TextField label="Quantité (unités de base)" type="number" min={1} max={ligne.disponible} value={quantite} onChange={(e) => setQuantite(e.target.value)} />
        <ErreurFormulaire erreur={m.error} />
      </div>
    </Modal>
  );
}

export function PageMouvements() {
  const [sp, setSp] = useSearchParams();
  const entrepotId = sp.get('entrepotId') ?? '';
  const type = sp.get('type') ?? '';
  const produitId = sp.get('produitId') ?? '';
  const page = Number(sp.get('page') ?? 1);
  const maj = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); if (k !== 'page') n.delete('page'); setSp(n, { replace: true }); };
  const r = useQuery({ queryKey: ['logistique', 'mouvements', entrepotId, type, produitId, page], queryFn: () => get<Page<Mouvement>>('/admin/mouvements', { entrepotId, type, produitId, page, taille: 50 }) });
  const exporter = () => r.data && telecharger('mouvements.csv', csvSur([['Date', 'Type', 'SKU', 'Produit', 'Entrepôt', 'Lot', 'Quantité', 'Motif', 'Auteur', 'Validé par'], ...r.data.donnees.map((m) => [m.creeLe, LIBELLE_MOUVEMENT[m.type] ?? m.type, m.sku, m.libelle, m.entrepotCode, m.numeroLot, m.quantite, m.motif, m.auteur, m.validePar])]));
  return (
    <>
      <EntetePage titre="Mouvements de stock" sousTitre="Journal en ajout seul : chaque mouvement indique le lot, l’auteur et le document source (traçabilité)." actions={<Button variante="secondaire" icone={Download} onClick={exporter} disabled={!r.data?.donnees.length}>Exporter la page (CSV)</Button>} />
      <Card>
        <div className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <SelectEntrepot valeur={entrepotId} onChange={(v) => maj('entrepotId', v)} />
          <SelectField label="Type" value={type} onChange={(e) => maj('type', e.target.value)} options={[{ value: '', label: 'Tous les types' }, ...Object.entries(LIBELLE_MOUVEMENT).map(([value, label]) => ({ value, label }))]} />
          {produitId && <div className="my-field"><span className="my-field__label">Produit filtré</span><Button variante="tertiaire" onClick={() => maj('produitId', '')}>Retirer le filtre produit</Button></div>}
        </div>
      </Card>
      <div className="my-pile" style={{ marginTop: 16 }}>
        <EtatRequete requete={r} vide={(d) => !d.donnees.length} titreVide="Aucun mouvement" enfants={(d) => (
          <>
            <Table<Mouvement>
              legende="Mouvements de stock"
              lignes={d.donnees}
              cle={(m) => m.id}
              colonnes={[
                { cle: 'd', titre: 'Date', rendu: (m) => dateHeure(m.creeLe) },
                { cle: 't', titre: 'Type', rendu: (m) => <Badge ton={m.quantite < 0 ? 'alerte' : 'info'}>{LIBELLE_MOUVEMENT[m.type] ?? m.type}</Badge> },
                { cle: 'p', titre: 'Produit', rendu: (m) => <>{m.libelle}<br /><span className="my-legende my-secondaire">{m.sku} · {m.entrepotCode} · lot {m.numeroLot}</span></> },
                { cle: 'q', titre: 'Quantité', num: true, rendu: (m) => (m.quantite > 0 ? `+${nombre(m.quantite)}` : nombre(m.quantite)) },
                { cle: 'm', titre: 'Motif', rendu: (m) => m.motif ?? '—' },
                { cle: 'a', titre: 'Auteur', rendu: (m) => <>{m.auteur}{m.validePar && <><br /><span className="my-legende">validé par {m.validePar}</span></>}</> },
              ]}
            />
            <Pagination page={d.page} total={d.total} taille={d.taille} onPage={(p) => maj('page', String(p))} />
          </>
        )} />
      </div>
    </>
  );
}

interface Transfert { id: string; reference?: string; source?: string; cible?: string; entrepotSourceId: string; entrepotCibleId: string; statut: string; creePar: string; creeLe: string; version: number; actionsPossibles: string[]; lignes: { produitId: string; lotId: string; quantite: number; libelle?: string; numeroLot?: string }[] }

export function PageTransferts() {
  const qc = useQueryClient();
  const toast = useToast();
  const { session } = useSession();
  const [statut, setStatut] = useState('');
  const r = useQuery({ queryKey: ['logistique', 'transferts', statut], queryFn: () => get<Page<Transfert>>('/admin/transferts', { statut, taille: 100 }) });
  const [confirm, setConfirm] = useState<{ t: Transfert; action: 'EXPEDIER' | 'RECEVOIR' | 'ANNULER' } | null>(null);
  const m = useMutation({
    mutationFn: (x: { t: Transfert; action: string }) => post(`/admin/transferts/${x.t.id}/transition`, { action: x.action }, { version: x.t.version }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['logistique'] }); toast({ ton: 'succes', titre: 'Transfert mis à jour' }); setConfirm(null); },
  });
  const portee = session?.porteeEntrepots;
  const peutAgir = (t: Transfert, a: string) => !portee?.length || portee.includes(a === 'RECEVOIR' ? t.entrepotCibleId : t.entrepotSourceId);
  const LIB = { EXPEDIER: 'Expédier', RECEVOIR: 'Réceptionner', ANNULER: 'Annuler' } as const;
  const action = (s: string) => (s === 'EXPEDIE' ? 'EXPEDIER' : s === 'RECU' ? 'RECEVOIR' : 'ANNULER') as 'EXPEDIER' | 'RECEVOIR' | 'ANNULER';
  return (
    <>
      <EntetePage titre="Transferts inter-entrepôts" sousTitre="Créez un transfert depuis le détail d’un lot. L’expédition sort le stock de l’entrepôt source ; la réception l’entre dans l’entrepôt cible." />
      <SelectField label="Statut" value={statut} onChange={(e) => setStatut(e.target.value)} options={[{ value: '', label: 'Tous' }, { value: 'BROUILLON', label: 'Brouillon' }, { value: 'EXPEDIE', label: 'Expédié' }, { value: 'RECU', label: 'Reçu' }, { value: 'ANNULE', label: 'Annulé' }]} />
      <div className="my-pile" style={{ marginTop: 16 }}>
        <EtatRequete requete={r} vide={(d) => !d.donnees.length} titreVide="Aucun transfert" texteVide="Ouvrez un lot depuis la page Stocks pour créer un transfert." enfants={(d) => (
          <Table<Transfert>
            legende="Transferts"
            lignes={d.donnees}
            cle={(t) => t.id}
            colonnes={[
              { cle: 'r', titre: 'Référence', rendu: (t) => <>{t.reference ?? t.id.slice(0, 8)}<br /><span className="my-legende my-secondaire">{dateHeure(t.creeLe)} · {t.creePar}</span></> },
              { cle: 'tr', titre: 'Trajet', rendu: (t) => <><Badge ton="marque">{t.source}</Badge> → <Badge ton="marque">{t.cible}</Badge></> },
              { cle: 'l', titre: 'Contenu', rendu: (t) => t.lignes.map((l) => <div key={l.lotId}>{nombre(l.quantite)} u. {l.libelle} <span className="my-legende">(lot {l.numeroLot})</span></div>) },
              { cle: 's', titre: 'Statut', rendu: (t) => <StatutBadge statut={t.statut} /> },
              {
                cle: 'a', titre: 'Actions', actions: true, rendu: (t) => (
                  <div className="my-ligne" style={{ justifyContent: 'flex-end' }}>
                    {t.actionsPossibles.map(action).filter((a) => peutAgir(t, a)).map((a) => (
                      <Button key={a} compact variante={a === 'ANNULER' ? 'tertiaire' : 'secondaire'} onClick={() => setConfirm({ t, action: a })}>{LIB[a]}</Button>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        )} />
      </div>
      {confirm && (
        <Confirmation ouverte titre={`${LIB[confirm.action]} le transfert`} texte={confirm.action === 'EXPEDIER' ? 'Le stock sera retiré de l’entrepôt source.' : confirm.action === 'RECEVOIR' ? 'Le stock sera ajouté à l’entrepôt cible (même lot).' : 'Le transfert sera annulé sans mouvement de stock.'} libelleAction={LIB[confirm.action]} danger={confirm.action === 'ANNULER'} onConfirmer={() => m.mutate(confirm)} onFermer={() => setConfirm(null)} chargement={m.isPending} erreur={m.error} />
      )}
    </>
  );
}

interface LotListe { id: string; numeroLot: string; ddm?: string | null; produit?: string; sku?: string; quantitePhysique: number; bloque: boolean }

export function PageLots() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const r = useQuery({ queryKey: ['logistique', 'lots', q, page], queryFn: () => get<Page<LotListe>>('/admin/lots', { q, page, taille: 25 }) });
  return (
    <>
      <EntetePage titre="Lots et traçabilité" sousTitre="Retrouvez un lot de la réception jusqu’aux commandes clientes (rappel produit)." />
      <SearchField label="Rechercher un numéro de lot, un produit ou un SKU" placeholder="Numéro de lot, produit ou SKU" onKeyDown={(e) => { if (e.key === 'Enter') { setQ((e.target as HTMLInputElement).value); setPage(1); } }} onBlur={(e) => { setQ(e.target.value); setPage(1); }} />
      <div className="my-pile" style={{ marginTop: 16 }}>
        <EtatRequete requete={r} vide={(d) => !d.donnees.length} titreVide="Aucun lot" enfants={(d) => (
          <>
            <Table<LotListe> legende="Lots" lignes={d.donnees} cle={(l) => l.id} colonnes={[
              { cle: 'n', titre: 'Lot', rendu: (l) => <><Link to={`/admin/lots/${l.id}`}>{l.numeroLot}</Link>{l.bloque && <> <Badge ton="erreur">Bloqué</Badge></>}</> },
              { cle: 'p', titre: 'Produit', rendu: (l) => <>{l.produit}<br /><span className="my-legende my-secondaire">{l.sku}</span></> },
              { cle: 'd', titre: 'DDM', rendu: (l) => dateCourte(l.ddm) },
              { cle: 'q', titre: 'En stock', num: true, rendu: (l) => nombre(l.quantitePhysique) },
            ]} />
            <Pagination page={d.page} total={d.total} taille={d.taille} onPage={setPage} />
          </>
        )} />
      </div>
    </>
  );
}

interface Tracabilite {
  lot: { id: string; numeroLot: string; ddm?: string | null; produitId: string; produit?: string; sku?: string; fournisseur?: string; bloque: boolean };
  reception?: { recueLe: string; recuePar?: string; entrepotCode?: string };
  stocks: { entrepotId: string; entrepotCode?: string; emplacement?: string; quantitePhysique: number; quantiteReservee: number; quantiteBloquee: number; disponible: number }[];
  mouvements: Mouvement[];
  commandes: { commandeId: string; reference: string; raisonSociale?: string; entrepriseReference?: string; statut: string; entrepotCode?: string; quantiteUnitesBase: number }[];
}

export function PageTracabilite() {
  const { lotId = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { peut } = useSession();
  const r = useQuery({ queryKey: ['logistique', 'tracabilite', lotId], queryFn: () => get<Tracabilite>(`/admin/lots/${lotId}/tracabilite`) });
  const [confirm, setConfirm] = useState(false);
  const bloquer = useMutation({
    mutationFn: async (motif: string) => {
      const d = r.data!;
      for (const s of d.stocks.filter((x) => x.disponible > 0)) {
        await post('/admin/stocks/ajustements', { produitId: d.lot.produitId, entrepotId: s.entrepotId, lotId: d.lot.id, type: 'BLOCAGE', quantite: s.disponible, motif: `Rappel produit : ${motif}` });
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['logistique'] }); toast({ ton: 'succes', titre: 'Lot bloqué', texte: 'Le disponible de ce lot est bloqué dans les entrepôts de votre portée.' }); setConfirm(false); },
  });
  const exporter = (d: Tracabilite) => telecharger(`tracabilite-${d.lot.numeroLot}.csv`, csvSur([['Commande', 'Client', 'Référence client', 'Entrepôt', 'Quantité (u.)', 'Statut'], ...d.commandes.map((c) => [c.reference, c.raisonSociale, c.entrepriseReference, c.entrepotCode, c.quantiteUnitesBase, c.statut])]));
  return (
    <EtatRequete requete={r} enfants={(d) => (
      <>
        <EntetePage
          titre={`Lot ${d.lot.numeroLot}`}
          sousTitre={`${d.lot.produit} (${d.lot.sku}) · DDM ${dateCourte(d.lot.ddm)}${d.lot.fournisseur ? ` · fournisseur ${d.lot.fournisseur}` : ''}`}
          fil={[{ libelle: 'Lots', vers: '/admin/lots' }, { libelle: d.lot.numeroLot }]}
          actions={<>
            <Button variante="secondaire" icone={Download} onClick={() => exporter(d)}>Exporter les clients livrés (CSV)</Button>
            {peut('stock.ajuster') && <Button variante="danger" icone={Ban} disabled={!d.stocks.some((s) => s.disponible > 0)} onClick={() => setConfirm(true)}>Bloquer le lot (rappel)</Button>}
          </>}
        />
        {d.lot.bloque && <Alert ton="erreur" titre="Lot bloqué">Ce lot n’est plus vendable ni préparable.</Alert>}
        <div className="my-pile my-pile--large" style={{ marginTop: 16 }}>
          <section className="my-pile">
            <h2 className="my-titre-4">1. Réception</h2>
            {d.reception ? <p>Reçu le {dateHeure(d.reception.recueLe)} à {d.reception.entrepotCode} par {d.reception.recuePar}</p> : <p className="my-secondaire">Stock initial ou import (pas de réception enregistrée).</p>}
          </section>
          <section className="my-pile">
            <h2 className="my-titre-4">2. Stocks actuels</h2>
            <Table legende="Stock du lot par entrepôt" lignes={d.stocks} cle={(s) => s.entrepotId} colonnes={[
              { cle: 'e', titre: 'Entrepôt', rendu: (s) => <Badge ton="marque">{s.entrepotCode}</Badge> },
              { cle: 'em', titre: 'Emplacement', rendu: (s) => s.emplacement ?? '—' },
              { cle: 'p', titre: 'Physique', num: true, rendu: (s) => nombre(s.quantitePhysique) },
              { cle: 'r', titre: 'Réservé', num: true, rendu: (s) => nombre(s.quantiteReservee) },
              { cle: 'b', titre: 'Bloqué', num: true, rendu: (s) => nombre(s.quantiteBloquee) },
              { cle: 'd', titre: 'Disponible', num: true, rendu: (s) => nombre(s.disponible) },
            ]} />
          </section>
          <section className="my-pile">
            <h2 className="my-titre-4">3. Commandes clientes servies ({d.commandes.length})</h2>
            {d.commandes.length ? (
              <Table legende="Commandes servies par ce lot" lignes={d.commandes} cle={(c) => c.commandeId} colonnes={[
                { cle: 'r', titre: 'Commande', rendu: (c) => c.reference },
                { cle: 'c', titre: 'Client', rendu: (c) => <>{c.raisonSociale}<br /><span className="my-legende my-secondaire">{c.entrepriseReference}</span></> },
                { cle: 'e', titre: 'Entrepôt', rendu: (c) => c.entrepotCode },
                { cle: 'q', titre: 'Quantité', num: true, rendu: (c) => nombre(c.quantiteUnitesBase) },
                { cle: 's', titre: 'Statut', rendu: (c) => <StatutBadge statut={c.statut} /> },
              ]} />
            ) : <p className="my-secondaire">Aucune commande n’a été servie avec ce lot.</p>}
          </section>
          <section className="my-pile">
            <h2 className="my-titre-4">4. Mouvements</h2>
            <Table legende="Mouvements du lot" lignes={d.mouvements} cle={(m) => m.id} colonnes={[
              { cle: 'd', titre: 'Date', rendu: (m) => dateHeure(m.creeLe) },
              { cle: 't', titre: 'Type', rendu: (m) => LIBELLE_MOUVEMENT[m.type] ?? m.type },
              { cle: 'e', titre: 'Entrepôt', rendu: (m) => m.entrepotCode },
              { cle: 'q', titre: 'Quantité', num: true, rendu: (m) => nombre(m.quantite) },
              { cle: 'm', titre: 'Motif', rendu: (m) => m.motif ?? '—' },
            ]} />
          </section>
        </div>
        <Confirmation ouverte={confirm} titre={`Bloquer le lot ${d.lot.numeroLot}`} texte="Tout le disponible de ce lot sera bloqué dans vos entrepôts : il ne pourra plus être réservé ni préparé. Les quantités déjà réservées restent à traiter commande par commande." libelleAction="Bloquer le lot" danger motif="obligatoire" onConfirmer={(m) => bloquer.mutate(m)} onFermer={() => setConfirm(false)} chargement={bloquer.isPending} erreur={bloquer.error} />
      </>
    )} />
  );
}

